'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/hooks/use-session';

export const CREDIT_BALANCE_QUERY_KEY = ['credits', 'balance'] as const;

export interface CreditBalance {
  balance: number;
  allowance: number;
  purchased: number;
  /** Credits consumed in the last 7 days — the basis for the forecast. */
  spent7d: number;
}

async function fetchCreditBalance(): Promise<CreditBalance> {
  const res = await fetch('/api/credits/balance');
  if (!res.ok) throw new Error(`/api/credits/balance returned ${res.status}`);
  const raw = (await res.json()) as Partial<CreditBalance> & {
    spent_7d?: number;
  };
  const json = raw;
  return {
    balance: typeof json.balance === 'number' ? json.balance : 0,
    allowance: typeof json.allowance === 'number' ? json.allowance : 0,
    purchased: typeof json.purchased === 'number' ? json.purchased : 0,
    spent7d: typeof raw.spent_7d === 'number' ? raw.spent_7d : 0,
  };
}

/**
 * Live credit snapshot for the header pill. Disabled for guests; refetches
 * lazily (the pill is ambient awareness, not a meter) — charges update the
 * cache instantly via {@link useApplyCreditBalance}, so the interval only
 * catches out-of-band changes like renewals or top-ups in another tab.
 */
export function useCreditBalance() {
  const { data: session } = useSession();
  return useQuery<CreditBalance>({
    queryKey: CREDIT_BALANCE_QUERY_KEY,
    queryFn: fetchCreditBalance,
    enabled: !!session?.user?.sub,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Push a fresh balance into the pill cache from a charge response, so the
 * number ticks down at the moment of spending without a refetch. Every
 * charge surface (Bilanz view headers, HTML export, register download)
 * already receives `{charged, balance}` — feed `balance` here.
 */
export function useApplyCreditBalance() {
  const queryClient = useQueryClient();
  return useCallback(
    (balance: number | null | undefined) => {
      if (typeof balance !== 'number' || !Number.isFinite(balance)) return;
      queryClient.setQueryData<CreditBalance>(
        CREDIT_BALANCE_QUERY_KEY,
        (prev) => ({
          balance,
          allowance: prev?.allowance ?? 0,
          // Mirror the backend invariant: the purchased wallet only shrinks
          // once the total balance falls below it.
          purchased: Math.min(prev?.purchased ?? 0, Math.max(balance, 0)),
          // The charge that produced this balance is itself recent spend, so
          // the forecast tightens immediately rather than waiting for the
          // next poll. `prev` may be undefined on a first-charge race.
          spent7d:
            (prev?.spent7d ?? 0) + Math.max((prev?.balance ?? balance) - balance, 0),
        }),
      );
    },
    [queryClient],
  );
}
